import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useLanguage } from "../../components/LanguageContext";
import {
  Language,
  License,
  LicenseUser,
  PatchLicense,
  RemovedLicenseUser,
} from "../../Types/types";
import { LicenseTranslation } from "../../Translations/LicenseTranslation";
import { AppDispatch } from "../../components/Manual_List/fetch/store";
import {
  fetchLicenseById,
  patchLicense,
  postLicense,
} from "../../components/Manual_List/fetch/slices";
import PackageListHeader from "../../components/Headers/PackageListHeader";
import { GeneralTranslation } from "../../Translations/GeneralTranslation";
import LicenseUsers from "./LicenseUsers";
import CircularProgress from "@mui/material/CircularProgress";
import { FaArrowLeft } from "react-icons/fa";

const AddLicense: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const { language } = useLanguage();
  const t = LicenseTranslation[language as Language];
  const gt = GeneralTranslation[language as Language];
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [numberOfUsers, setNumberOfUsers] = useState<number>(1);
  const [expiredAt, setExpiredAt] = useState("");
  const [addedUsers, setAddedUsers] = useState<LicenseUser[] | null>();
  const [removedUsers, setRemovedUsers] = useState<
    RemovedLicenseUser[] | null
  >();

  const [isLoading, setIsLoading] = useState(false);

  const formatDate = (dateString: any) => {
    if (dateString) {
      const date = new Date(dateString.date);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
    return "";
  };

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      dispatch(fetchLicenseById(id)).then((result) => {
        if (result.payload) {
          const licenseData = result.payload as License;

          setName(licenseData.name || "");
          setEmail(licenseData.email || "");
          setNumberOfUsers(licenseData.numberOfAccounts || 1);
          setExpiredAt(formatDate(licenseData.expiredAt) || "");
        }
      });
      setIsLoading(false);
    }
  }, [id, dispatch]);

  const handleNumberOfUsers = (event: React.ChangeEvent<HTMLInputElement>) => {
    const numberOfUsers = event.target.valueAsNumber;
    if (isNaN(numberOfUsers) || numberOfUsers <= 0) {
      setNumberOfUsers(1);
    } else {
      setNumberOfUsers(numberOfUsers);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    if (id) {
      const patchLicenseData = {
        id: parseInt(id),
        licenseData: {
          name: name,
          email: email,
          numberOfUsers: numberOfUsers,
          expiredAt: expiredAt.length > 0 ? expiredAt : null,
        },
      };
      const patchResultAction = await dispatch(patchLicense(patchLicenseData));

      if (!patchLicense.fulfilled.match(patchResultAction)) {
        console.error("Failed to patch license:", patchResultAction.payload);
        return;
      }

      const resultPayload = patchResultAction.payload as PatchLicense;
      if (
        resultPayload.accounts?.addedAccounts ||
        resultPayload.accounts?.removedAccounts
      ) {
        setAddedUsers(resultPayload.accounts?.addedAccounts);
        setRemovedUsers(resultPayload.accounts?.removedAccounts);
      } else {
        navigate("/licenses-list");
      }
    } else {
      const licenseData = {
        name: name,
        email: email,
        numberOfUsers: numberOfUsers,
        expiredAt: expiredAt,
      };
      const resultAction = await dispatch(postLicense(licenseData));

      if (!postLicense.fulfilled.match(resultAction)) {
        console.error("Failed to post license:", resultAction.payload);
        return;
      }

      setAddedUsers(resultAction.payload.accounts);
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen w-screen flex flex-col bg-gray-100 dark:bg-darkBackground dark:text-white">
      <PackageListHeader />
      <div className="flex flex-col items-center justify-start flex-grow">
        {addedUsers ? (
          <LicenseUsers
            users={addedUsers}
            label={t.addedUsers.replace("{licenseName}", name)}
          />
        ) : removedUsers ? (
          <LicenseUsers
            users={removedUsers}
            label={t.removedUsers.replace("{licenseName}", name)}
            isPassword={false}
          />
        ) : (
          <form
            id="add-package-form"
            className="w-full px-4 sm:px-6 md:px-8 py-6 max-w-9xl flex flex-col relative"
            onSubmit={handleSubmit}
          >
            <div className="flex items-center mb-2">
              <FaArrowLeft
                className="text-gray-600 cursor-pointer mr-2"
                onClick={() => navigate("/licenses-list/")}
              />
              <h1 className="text-xl font-semibold">
                {id ? t.titleEdit : t.titleAdd}
              </h1>
            </div>
            <div className="mb-2 relative">
              <label htmlFor="name" className="block mb-1 text-base ml-2">
                {gt.name}
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="name"
                  className="w-full pl-3 pr-3 py-2 rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-0 dark:border dark:border-purple dark:bg-darkBackground"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="mb-2 relative">
              <label htmlFor="email" className="block mb-1 text-base ml-2">
                {t.email}
              </label>
              <div className="relative">
                <input
                  id="email"
                  className="w-full pl-3 pr-3 py-2 rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-0 dark:border dark:border-purple dark:bg-darkBackground"
                  value={email}
                  type={"email"}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="mb-4">
              <label
                htmlFor="numberOfUsers"
                className="block mb-1 text-base ml-2"
              >
                {t.numberOfUsers}
              </label>
              <div className="relative">
                <input
                  id="numberOfUsers"
                  className="w-full pl-3 pr-3 py-2 rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-0 dark:border dark:border-purple dark:bg-darkBackground"
                  value={numberOfUsers}
                  type={"number"}
                  onChange={(e) => handleNumberOfUsers(e)}
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="mb-4">
              <label htmlFor="validUntil" className="block mb-1 text-base ml-2">
                {t.validUntil}
              </label>
              <div className="relative">
                <input
                  id="validUntil"
                  className="w-full pl-3 pr-3 py-2 rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-0 dark:border dark:border-purple dark:bg-darkBackground dark:text-white"
                  type={"date"}
                  value={expiredAt || ""}
                  onFocus={(event) => event.target.showPicker()}
                  onChange={(e) => setExpiredAt(e.target.value)}
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="absolute bottom-0 -mb-4 right-8 flex space-x-2">
              <button
                type="button"
                className="bg-gray-300 dark:bg-white dark:text-black text-gray-700 px-4 py-2 rounded font-medium shadow-sm"
                onClick={() => navigate(-1)}
              >
                {gt.discard}
              </button>
              <button
                type="submit"
                className="bg-purple text-white px-4 py-2 rounded font-medium shadow-sm"
                form="add-package-form"
              >
                {gt.save}
              </button>
            </div>
          </form>
        )}
      </div>
      {isLoading && (
        <div className="absolute inset-0 bg-gray-100 bg-opacity-75 flex justify-center items-center z-50">
          <CircularProgress />
        </div>
      )}
    </div>
  );
};

export default AddLicense;
